import { and, asc, desc, eq, getTableColumns, gte, isNotNull, isNull, or, sql } from "drizzle-orm";

import { createLogger } from "@/logger";

const log = createLogger("feed");

import { db } from "@/db/db";
import {
  account,
  accountEvent,
  audioEncoding,
  broadcastAction,
  broadcastRecipient,
  callRecord,
  displayArtifact,
  file,
  item,
  link,
  permission,
  pipelineArtifactMetadata,
  scheduleTrigger,
  transcription,
  workflowItem,
  workspaceCommandAlias,
  workspaceMembership,
  wsMembershipAvailability,
} from "@/db/schema";
import { emptyQuery } from "@/models/emptyQuery.ts";

export function getItems(currentFeedId?: string) {
  const query = db.query.item.findMany({
    where: and(eq(item.feedId, currentFeedId), isNull(item.deletedAt)),
    orderBy: asc(item.createdAt),
  });
  if (!currentFeedId) {
    log.info("FEED LOADING: Items Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

export function getTranscription(currentFeedId?: string, selectedLanguage?: string) {
  const query = db
    .select({
      ...getTableColumns(transcription),
    })
    .from(transcription)
    .innerJoin(
      item,
      and(
        eq(transcription.contentId, item.contentId),
        eq(item.feedId, currentFeedId),
        eq(transcription.language, selectedLanguage),
      ),
    );

  if (!currentFeedId || !selectedLanguage) {
    log.info("FEED LOADING: Transcriptions Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

export function getAudioEncoding(currentFeedId?: string) {
  const query = db
    .select({
      ...getTableColumns(audioEncoding),
    })
    .from(audioEncoding)
    .innerJoin(item, and(eq(audioEncoding.contentId, item.contentId), eq(item.feedId, currentFeedId)));

  if (!currentFeedId) {
    log.info("FEED LOADING: Audio Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

export function getDisplayArtifacts(currentFeedId?: string) {
  const query = db
    .select({
      ...getTableColumns(displayArtifact),
    })
    .from(displayArtifact)
    .innerJoin(item, and(eq(displayArtifact.contentId, item.contentId), eq(item.feedId, currentFeedId)));
  if (!currentFeedId) {
    log.info("FEED LOADING: Display artifacts Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

export function getCallRecords(currentFeedId?: string) {
  const query = db
    .select({
      ...getTableColumns(callRecord),
    })
    .from(callRecord)
    .innerJoin(item, and(eq(callRecord.contentId, item.contentId), eq(item.feedId, currentFeedId)));
  if (!currentFeedId) {
    log.info("FEED LOADING: Call records Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

export function getFiles(currentFeedId?: string) {
  const query = db
    .select({
      ...getTableColumns(file),
    })
    .from(file)
    .innerJoin(item, and(eq(file.contentId, item.contentId), eq(item.feedId, currentFeedId)))
    .groupBy(file.id);

  if (!currentFeedId) {
    log.info("FEED LOADING: Files Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

export function getLinks(currentFeedId?: string) {
  const query = db
    .select({
      ...getTableColumns(link),
    })
    .from(link)
    .innerJoin(item, and(eq(link.contentId, item.contentId), eq(item.feedId, currentFeedId)));

  if (!currentFeedId) {
    log.info("FEED LOADING: Links Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

export function getPams(currentFeedId?: string) {
  const query = db
    .select({
      ...getTableColumns(pipelineArtifactMetadata),
    })
    .from(pipelineArtifactMetadata)
    .innerJoin(item, and(eq(pipelineArtifactMetadata.contentId, item.contentId), eq(item.feedId, currentFeedId)));

  if (!currentFeedId) {
    log.info("FEED LOADING: Pam Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}
export function workspaceAvailabilitySize(workspaceId: string) {
  const query = db.select().from(wsMembershipAvailability).where(eq(wsMembershipAvailability.workspaceId, workspaceId));
  if (!workspaceId) {
    log.info("No Workspace Id provided for workspaceAvailability");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

export const workspacePermissionsQuery = (workspaceId: string) => {
  const query = db
    .select({ id: permission.id })
    .from(permission)
    .innerJoin(
      workspaceMembership,
      and(
        eq(permission.workspaceMembershipId, workspaceMembership.id),
        eq(workspaceMembership.workspaceId, workspaceId),
      ),
    );
  return query;
};

export function workspaceAccountsWithStatusSelect(workspaceId?: string) {
  const query = db
    .selectDistinct({
      ...getTableColumns(wsMembershipAvailability),
      ...getTableColumns(account),
      alias: workspaceCommandAlias.alias,
      role: workspaceMembership.role,
      workspaceMembershipId: sql`${workspaceMembership.id} as "workspaceMembershipId"`,
    })
    .from(account)
    .innerJoin(
      workspaceMembership,
      and(
        eq(account.id, workspaceMembership.accountId),
        eq(workspaceMembership.workspaceId, workspaceId),
        // eq(workspaceMembership.status, "active"),
      ),
    )
    .leftJoin(
      workspaceCommandAlias,
      and(
        eq(workspaceCommandAlias.workspaceMembershipId, workspaceMembership.id),
        eq(workspaceMembership.workspaceId, workspaceId),
        isNotNull(workspaceCommandAlias.feedId),
      ),
    )
    .leftJoin(wsMembershipAvailability, eq(wsMembershipAvailability.workspaceMembershipId, workspaceMembership.id));

  if (!workspaceId) {
    log.info("FEED LOADING: Workspace Account Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

export function eventsForFeedItems(currentFeedId?: string) {
  const query = db
    .selectDistinct({
      ...getTableColumns(accountEvent),
    })
    .from(accountEvent)
    .innerJoin(item, and(eq(item.feedId, currentFeedId), eq(accountEvent.itemId, item.id)));
  if (!currentFeedId) {
    log.info("FEED LOADING: Events Returning empty query");
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

// Reactive probe used by FeedContextProvider to derive a channel's window
// anchor: the createdAt of the (pageCount * pageSize)th newest non-deleted
// item in the feed. Returns no rows once the window covers the channel's
// full local history. Cheap (one indexed lookup) and runs reactively, so
// it stays in sync with bulk-upserts and live-tail inserts.
export function feedWindowAnchorQuery(currentFeedId?: string, offset = 0) {
  const query = db
    .select({ createdAt: item.createdAt })
    .from(item)
    .where(and(eq(item.feedId, currentFeedId ?? ""), isNull(item.deletedAt)))
    .orderBy(desc(item.createdAt))
    .limit(1)
    .offset(offset);

  if (!currentFeedId) {
    return emptyQuery as unknown as typeof query;
  }
  return query;
}

// Combined query to fetch all feed content in a single database call. The
// optional `windowStartTimestamp` bounds every join to items with
// `createdAt >= windowStartTimestamp`, capping the inherent cartesian
// product of these LEFT JOINs (every join keys back to item, not to each
// other) at `window_size * per_item_fanout` instead of
// `total_items * per_item_fanout`. Without it, busy long-lived channels
// produce row counts in the millions and crash the in-browser PGlite
// worker — see WEB-1985.
export function getAllFeedContent(currentFeedId?: string, selectedLanguage?: string, windowStartTimestamp?: string) {
  if (!currentFeedId || !selectedLanguage) {
    log.info("FEED LOADING: getAllFeedContent Returning empty query");
    return emptyQuery as unknown as ReturnType<typeof query.execute>;
  }

  // Single query with all LEFT JOINs to fetch all content types at once.
  // The window predicate lives on the `from(item)` WHERE rather than each
  // join's ON so it's evaluated before the cartesian product expands —
  // otherwise PGlite would still materialize every joined row before
  // filtering, defeating the point.
  const query = db
    .select({
      // Item columns
      item: getTableColumns(item),
      // Content columns
      transcription: getTableColumns(transcription),
      audioEncoding: getTableColumns(audioEncoding),
      displayArtifact: getTableColumns(displayArtifact),
      file: getTableColumns(file),
      link: getTableColumns(link),
      pam: getTableColumns(pipelineArtifactMetadata),
      callRecord: getTableColumns(callRecord),
      accountEvent: getTableColumns(accountEvent),
    })
    .from(item)
    .leftJoin(
      transcription,
      and(eq(transcription.contentId, item.contentId), eq(transcription.language, selectedLanguage)),
    )
    .leftJoin(audioEncoding, eq(audioEncoding.contentId, item.contentId))
    .leftJoin(displayArtifact, eq(displayArtifact.contentId, item.contentId))
    .leftJoin(file, eq(file.contentId, item.contentId))
    .leftJoin(link, eq(link.contentId, item.contentId))
    .leftJoin(pipelineArtifactMetadata, eq(pipelineArtifactMetadata.contentId, item.contentId))
    .leftJoin(callRecord, eq(callRecord.contentId, item.contentId))
    .leftJoin(accountEvent, eq(accountEvent.itemId, item.id))
    .where(
      and(
        eq(item.feedId, currentFeedId),
        isNull(item.deletedAt),
        windowStartTimestamp ? gte(item.createdAt, windowStartTimestamp) : undefined,
      ),
    )
    .orderBy(asc(item.createdAt));

  return query;
}

// Transform flat results from getAllFeedContent into grouped structure
export function groupFeedContentResults(rows: any[]) {
  if (!rows || rows.length === 0) {
    return {
      items: [],
      transcriptions: [],
      audioEncodings: [],
      displayArtifacts: [],
      files: [],
      links: [],
      pams: [],
      callRecords: [],
      events: [],
    };
  }

  const itemsMap = new Map();
  const transcriptionsMap = new Map<string, any[]>();
  const audioEncodingsMap = new Map<string, any[]>();
  const displayArtifactsMap = new Map<string, any[]>();
  const filesMap = new Map<string, any[]>();
  const linksMap = new Map<string, any[]>();
  const pamsMap = new Map<string, any[]>();
  const callRecordsMap = new Map<string, any[]>();
  const eventsMap = new Map<string, any[]>();

  for (const row of rows) {
    // Group items (deduplicate by item ID)
    if (row.item && !itemsMap.has(row.item.id)) {
      itemsMap.set(row.item.id, row.item);
    }

    // Group content by contentId (handling nulls from LEFT JOINs)
    const contentId = row.item?.contentId;
    if (contentId) {
      if (row.transcription?.id) {
        const list = transcriptionsMap.get(contentId) || [];
        // Deduplicate by transcription ID
        if (!list.some((t) => t.id === row.transcription.id)) {
          list.push(row.transcription);
          transcriptionsMap.set(contentId, list);
        }
      }

      if (row.audioEncoding?.id) {
        const list = audioEncodingsMap.get(contentId) || [];
        if (!list.some((a) => a.id === row.audioEncoding.id)) {
          list.push(row.audioEncoding);
          audioEncodingsMap.set(contentId, list);
        }
      }

      if (row.displayArtifact?.id) {
        const list = displayArtifactsMap.get(contentId) || [];
        if (!list.some((d) => d.id === row.displayArtifact.id)) {
          list.push(row.displayArtifact);
          displayArtifactsMap.set(contentId, list);
        }
      }

      if (row.file?.id) {
        const list = filesMap.get(contentId) || [];
        if (!list.some((f) => f.url === row.file.url)) {
          list.push(row.file);
          filesMap.set(contentId, list);
        }
      }

      if (row.link?.id) {
        const list = linksMap.get(contentId) || [];
        if (!list.some((l) => l.id === row.link.id)) {
          list.push(row.link);
          linksMap.set(contentId, list);
        }
      }

      if (row.pam?.id) {
        const list = pamsMap.get(contentId) || [];
        if (!list.some((p) => p.id === row.pam.id)) {
          list.push(row.pam);
          pamsMap.set(contentId, list);
        }
      }

      if (row.callRecord?.id) {
        const list = callRecordsMap.get(contentId) || [];
        if (!list.some((c) => c.id === row.callRecord.id)) {
          list.push(row.callRecord);
          callRecordsMap.set(contentId, list);
        }
      }
    }

    // Group events by item ID
    const itemId = row.item?.id;
    if (itemId && row.accountEvent?.id) {
      const list = eventsMap.get(itemId) || [];
      if (!list.some((e) => e.id === row.accountEvent.id)) {
        list.push(row.accountEvent);
        eventsMap.set(itemId, list);
      }
    }
  }

  return {
    items: Array.from(itemsMap.values()),
    transcriptions: Array.from(transcriptionsMap.values()).flat(),
    audioEncodings: Array.from(audioEncodingsMap.values()).flat(),
    displayArtifacts: Array.from(displayArtifactsMap.values()).flat(),
    files: Array.from(filesMap.values()).flat(),
    links: Array.from(linksMap.values()).flat(),
    pams: Array.from(pamsMap.values()).flat(),
    callRecords: Array.from(callRecordsMap.values()).flat(),
    events: Array.from(eventsMap.values()).flat(),
  };
}

interface artifact {
  contentId: string;
}
export function mapForArtifact<T extends artifact>(rows: T[], debugName?: string) {
  // logger(["Generating map for artifact", debugName]);
  const map = new Map<string, T[]>();
  for (const r of rows) {
    if (map.has(r.contentId)) {
      const t = map.get(r.contentId);
      t.push(r);
    } else {
      map.set(r.contentId, [r]);
    }
  }
  return map;
}
interface hasId {
  id: string;
}
export function mapForHasId<T extends hasId>(rows: T[], debugName?: string) {
  // logger(["Generating map for hasId", debugName]);
  const map = new Map<string, T>(rows.map((r) => [r.id, r]));
  return map;
}

interface hasItemId {
  itemId: string;
}
export function mapForHasItemId<T extends hasItemId>(rows: T[]) {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    if (map.has(r.itemId)) {
      const t = map.get(r.itemId);
      t.push(r);
    } else {
      map.set(r.itemId, [r]);
    }
  }
  return map;
}

interface hasWorkspaceMembershipId {
  workspaceMembershipId: string;
}
export function mapForHasWorkspaceMembershipIdMany<T extends hasWorkspaceMembershipId>(rows: T[] = []) {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    if (map.has(r.workspaceMembershipId)) {
      const t = map.get(r.workspaceMembershipId);
      t.push(r);
    } else {
      map.set(r.workspaceMembershipId, [r]);
    }
  }
  return map;
}

export function mapForHasWorkspaceMembershipId<T extends hasWorkspaceMembershipId>(rows: T[]) {
  const map = new Map<string, T>(rows.map((r) => [r.workspaceMembershipId, r]));
  return map;
}

// Combined query to fetch all workflow content in a single database call
export function getAllWorkflowContent(workspaceId?: string, selectedLanguage?: string, date?: string) {
  if (!workspaceId || !selectedLanguage || !date) {
    log.info("WORKFLOW LOADING: getAllWorkflowContent Returning empty query");
    return emptyQuery as unknown as ReturnType<typeof query.execute>;
  }

  // Single query with all LEFT JOINs to fetch all workflow-related data at once
  const query = db
    .select({
      // Workflow item columns
      workflowItem: getTableColumns(workflowItem),
      // Broadcast action columns
      broadcastAction: getTableColumns(broadcastAction),
      // Schedule trigger columns
      scheduleTrigger: getTableColumns(scheduleTrigger),
      // Broadcast recipient columns (we'll need to handle multiple recipients per broadcast)
      broadcastRecipient: getTableColumns(broadcastRecipient),
      // Content columns
      transcription: getTableColumns(transcription),
      file: getTableColumns(file),
      displayArtifact: getTableColumns(displayArtifact),
    })
    .from(workflowItem)
    .leftJoin(broadcastAction, eq(broadcastAction.workflowItemId, workflowItem.id))
    .leftJoin(scheduleTrigger, eq(scheduleTrigger.broadcastActionId, broadcastAction.id))
    .leftJoin(broadcastRecipient, eq(broadcastRecipient.broadcastId, broadcastAction.broadcastId))
    .leftJoin(
      transcription,
      and(eq(transcription.contentId, workflowItem.contentId), eq(transcription.language, selectedLanguage)),
    )
    .leftJoin(file, eq(file.contentId, workflowItem.contentId))
    .leftJoin(displayArtifact, eq(displayArtifact.contentId, workflowItem.contentId))
    .where(
      and(
        eq(workflowItem.workspaceId, workspaceId),
        isNull(workflowItem.deletedAt),
        or(isNull(scheduleTrigger.oneTimeSchedule), sql`${scheduleTrigger.oneTimeSchedule} >= ${date}`),
      ),
    )
    .orderBy(desc(workflowItem.createdAt));

  return query;
}

// Transform flat results from getAllWorkflowContent into grouped structure
export function groupWorkflowContentResults(rows: any[]): {
  workflowItems: any[];
  broadcastActions: any[];
  scheduleTriggers: any[];
  broadcastRecipients: Map<string, any[]>;
  transcriptions: Map<string, any[]>;
  files: Map<string, any[]>;
  displayArtifacts: Map<string, any[]>;
} {
  if (!rows || rows.length === 0) {
    return {
      workflowItems: [],
      broadcastActions: [],
      scheduleTriggers: [],
      broadcastRecipients: new Map(),
      transcriptions: new Map(),
      files: new Map(),
      displayArtifacts: new Map(),
    };
  }

  const workflowItemsMap = new Map();
  const broadcastActionsMap = new Map();
  const scheduleTriggersMap = new Map();
  const broadcastRecipientsMap = new Map<string, any[]>();
  const transcriptionsMap = new Map<string, any[]>();
  const filesMap = new Map<string, any[]>();
  const displayArtifactsMap = new Map<string, any[]>();

  for (const row of rows) {
    // Group workflow items (deduplicate by item ID)
    if (row.workflowItem && !workflowItemsMap.has(row.workflowItem.id)) {
      workflowItemsMap.set(row.workflowItem.id, row.workflowItem);
    }

    // Group broadcast actions (deduplicate by action ID)
    if (row.broadcastAction && !broadcastActionsMap.has(row.broadcastAction.id)) {
      broadcastActionsMap.set(row.broadcastAction.id, row.broadcastAction);
    }

    // Group schedule triggers (deduplicate by trigger ID)
    if (row.scheduleTrigger && !scheduleTriggersMap.has(row.scheduleTrigger.id)) {
      scheduleTriggersMap.set(row.scheduleTrigger.id, row.scheduleTrigger);
    }

    // Group content by workflowItem ID and contentId
    const workflowItemId = row.workflowItem?.id;
    const contentId = row.workflowItem?.contentId;

    if (contentId) {
      // Group transcriptions
      if (row.transcription?.id) {
        const list = transcriptionsMap.get(contentId) || [];
        if (!list.some((t) => t.id === row.transcription.id)) {
          list.push(row.transcription);
          transcriptionsMap.set(contentId, list);
        }
      }

      // Group files
      if (row.file?.id) {
        const list = filesMap.get(contentId) || [];
        if (!list.some((f) => f.id === row.file.id)) {
          list.push(row.file);
          filesMap.set(contentId, list);
        }
      }

      // Group display artifacts
      if (row.displayArtifact?.id) {
        const list = displayArtifactsMap.get(contentId) || [];
        if (!list.some((d) => d.id === row.displayArtifact.id)) {
          list.push(row.displayArtifact);
          displayArtifactsMap.set(contentId, list);
        }
      }
    }

    // Group broadcast recipients by broadcast ID
    if (row.broadcastRecipient?.id && row.broadcastAction?.broadcastId) {
      const broadcastId = row.broadcastAction.broadcastId;
      const list = broadcastRecipientsMap.get(broadcastId) || [];
      if (!list.some((r) => r.id === row.broadcastRecipient.id)) {
        list.push(row.broadcastRecipient);
        broadcastRecipientsMap.set(broadcastId, list);
      }
    }
  }

  return {
    workflowItems: Array.from(workflowItemsMap.values()),
    broadcastActions: Array.from(broadcastActionsMap.values()),
    scheduleTriggers: Array.from(scheduleTriggersMap.values()),
    broadcastRecipients: broadcastRecipientsMap,
    transcriptions: transcriptionsMap,
    files: filesMap,
    displayArtifacts: displayArtifactsMap,
  };
}
